import {useCallback} from 'react';
import {useDispatch} from 'react-redux';

import {useCachedTable} from 'src/hooks/common-data-service/cached-table';
import {getAccountUsageReports} from 'src/openapi/admin/apis/UsageReportsMetadataApi';
import {ReportType} from 'src/openapi/admin/models/ReportType';
import {useAppDispatch} from 'src/store/hooks';

import {excelPluginUsageReportsStateSelector} from './slices/excel-plugin-usage-reports-state.slice';
import {
    excelPluginUsageReportsActions,
    excelPluginUsageReportsDataSelector,
    excelPluginUsageReportsResultSelector,
} from './slices/excel-plugin-usage-reports.slice';

import type {CacheKeys} from 'src/hooks/common-data-service/cached-table';
import type {ReportMetadataPage} from 'src/openapi/admin/models/ReportMetadataPage';

type GetUsageReportsCacheKeys = CacheKeys<undefined, undefined, undefined>;

type Props = {
    accountId: number,
};

export const useGetExcelPluginUsageReports = ({accountId}: Props) => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();

    const getAction = useCallback((cacheKeys: GetUsageReportsCacheKeys) => apiDispatch(
        getAccountUsageReports({
            accountId,
            reportMetadataRequest: {
                page: cacheKeys.pagination.currentPage,
                pageSize: cacheKeys.pagination.pageSize,
                reportType: ReportType.EXCEL_REPORT,
            },
        }),
    ), [accountId, apiDispatch]);

    const setLoadedData = useCallback((
        response: ReportMetadataPage,
        cacheKeys: GetUsageReportsCacheKeys,
    ) => {
        dispatch(excelPluginUsageReportsActions.onSuccess({
            cacheKeys,
            data: response.reportsMetadata,
            totalCount: response.totalReports,
        }));
    }, [dispatch]);

    return useCachedTable({
        dataSelector: excelPluginUsageReportsDataSelector,
        stateSelector: excelPluginUsageReportsStateSelector,
        resultSelector: excelPluginUsageReportsResultSelector,
        actions: excelPluginUsageReportsActions,
        getAction,
        onSuccessLoad: setLoadedData,
    });
};
